import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useEffect, useRef } from 'react';
import { APPFOLIO_PUSH_STATUS } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { useStore } from 'src/stores';
import { getAppfolioPushRowState } from './appfolioPushRow';
const POLL_INTERVAL_MS = 5000;

/**
 * Caps, matching what the uploaded-bill poller already uses. A pending push can be stuck for good — a
 * Kafka publish that failed leaves a row nothing will ever finish, and recovering it is a re-click — so
 * an unbounded poll would ask forever about a document that is never going to answer.
 */
const MAX_POLLS = 120;
const MAX_FAILED_POLLS = 3;

/**
 * The one bill the details view is showing, read from `appfolio-push-status` rather than from the list.
 * Two reasons it reads for itself:
 *
 * - The list poll deliberately rests while this view is open, so nothing else is watching this bill.
 * - The list row carries the verdict but no attempts, and the blockers behind a failure — the only place
 *   the way out of `partial_invoice` or `amount_mismatch` is stated — live on the attempt.
 *
 * Until the first read lands, the row's own verdict stands in, so the view opens with something true
 * rather than blank.
 */
export const useBillApPushDetails = _ref => {
  let billId = _ref.billId,
    isEnabled = _ref.isEnabled,
    listApPush = _ref.listApPush;
  const store = useStore();
  const pollCountRef = useRef(0);
  const failedPollCountRef = useRef(0);
  const readStatus = billId ? store.appfolioPush.getStatus(billId) : null;
  const hasReadStatus = !!billId && store.appfolioPush.hasReadStatus(billId);
  const rowStatus = listApPush && billId ? _objectSpread(_objectSpread({}, listApPush), {}, {
    billId,
    attempts: []
  }) : null;

  // The read wins once it lands, because it carries the attempts the row cannot. Before that — and
  // again after a trigger drops it — the row's verdict stands in: the same verdict, off the same
  // ledger, just without the detail behind it.
  const pushStatus = readStatus !== null && readStatus !== void 0 ? readStatus : rowStatus;

  /**
   * Arming the poll is part of the effect's identity, not just something it decides once. A view opened
   * on an unpushed document reads once and rests; the push that starts a moment later has to wake it,
   * and it does so by turning this true.
   */
  const isOnItsWay = (pushStatus === null || pushStatus === void 0 ? void 0 : pushStatus.status) === APPFOLIO_PUSH_STATUS.pending;
  useEffect(() => {
    if (!isEnabled || !billId) return undefined;

    // Settled, and already read for itself: nothing to ask. This is the pending push having just
    // landed, which re-runs the effect only to find there is no longer anything to wait for.

    let isActive = true;
    let timeoutId = null;
    pollCountRef.current = 0;
    failedPollCountRef.current = 0;

    /**
     * A read that failed is worth repeating whatever the bill's state was — the answer is unknown, not
     * settled. A read that succeeded is worth repeating only while the push is `pending`: a failure and a
     * reconciliation both wait on a person, so asking again would never change either.
     */
    const isWorthAskingAgain = didRead => {
      var _store$appfolioPush$g;
      return pollCountRef.current < MAX_POLLS && failedPollCountRef.current < MAX_FAILED_POLLS && (!didRead || ((_store$appfolioPush$g = store.appfolioPush.getStatus(billId)) === null || _store$appfolioPush$g === void 0 ? void 0 : _store$appfolioPush$g.status) === APPFOLIO_PUSH_STATUS.pending);
    };

    /**
     * Chained rather than scheduled on a fixed interval: `run` cancels the request still in flight, so a
     * tick landing mid-pass would abort it. The gap is measured from the end of one read to the next.
     */
    const read = async () => {
      pollCountRef.current += 1;
      const resp = await store.appfolioPush.readPushStatusesAsync.run({
        billIds: [billId]
      });
      if (!isActive) return;
      const didRead = !!(resp !== null && resp !== void 0 && resp.isSuccessful);
      failedPollCountRef.current = didRead ? 0 : failedPollCountRef.current + 1;
      if (isWorthAskingAgain(didRead)) timeoutId = setTimeout(read, POLL_INTERVAL_MS);
    };
    read();
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, billId, isOnItsWay]);
  return {
    pushStatus,
    getState: function (billExtractStatus) {
      let invoiceNumber = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
      return getAppfolioPushRowState({
        billExtractStatus,
        hasReadStatus: hasReadStatus || !!rowStatus,
        invoiceNumber,
        isPushing: store.appfolioPush.pushBillAsync.inProgress,
        pushStatus
      });
    }
  };
};